/**
 * Local activation funnel timestamps — essential, first-party only.
 * Used to measure time-to-value without third-party analytics.
 */

import { storage } from "@/features/app-context/utils";
import { hasOptionalStorageConsent } from "@/lib/privacyConsent";

const KEYS = {
  firstOpen: "or:activation:first_open",
  signupAt: "or:activation:signup_at",
  firstDayMarked: "or:activation:first_day_marked",
  lastActiveDate: "or:activation:last_active_date",
  d1Reported: "or:activation:d1_reported",
  d7CandidateReported: "or:activation:d7_candidate_reported",
} as const;

export function recordFirstAppOpen(): void {
  if (!hasOptionalStorageConsent()) return;
  if (!storage.get(KEYS.firstOpen)) {
    storage.set(KEYS.firstOpen, new Date().toISOString());
  }
}

export function recordSignup(): void {
  if (!hasOptionalStorageConsent()) return;
  if (!storage.get(KEYS.signupAt)) {
    storage.set(KEYS.signupAt, new Date().toISOString());
  }
}

export function msSinceFirstOpen(): number | null {
  const raw = storage.get(KEYS.firstOpen);
  if (!raw) return null;
  const elapsed = Date.now() - new Date(raw).getTime();
  return Number.isFinite(elapsed) ? elapsed : null;
}

export function daysSinceFirstOpen(): number | null {
  const raw = storage.get(KEYS.firstOpen);
  if (!raw) return null;
  const elapsed = Date.now() - new Date(raw).getTime();
  if (!Number.isFinite(elapsed)) return null;
  return Math.floor(elapsed / 86_400_000);
}

export function recordActiveSession(): {
  activeDate: string;
  daysSinceFirstOpen: number | null;
  shouldReportD1: boolean;
  shouldReportD7Candidate: boolean;
} {
  const activeDate = new Date().toISOString().slice(0, 10);
  if (!hasOptionalStorageConsent()) {
    return {
      activeDate,
      daysSinceFirstOpen: null,
      shouldReportD1: false,
      shouldReportD7Candidate: false,
    };
  }
  storage.set(KEYS.lastActiveDate, activeDate);

  const ageDays = daysSinceFirstOpen();
  const shouldReportD1 =
    ageDays !== null &&
    ageDays >= 1 &&
    storage.get(KEYS.d1Reported) !== "1";
  const shouldReportD7Candidate =
    ageDays !== null &&
    ageDays >= 7 &&
    storage.get(KEYS.d7CandidateReported) !== "1";

  return {
    activeDate,
    daysSinceFirstOpen: ageDays,
    shouldReportD1,
    shouldReportD7Candidate,
  };
}

export function markD1Reported(): void {
  storage.set(KEYS.d1Reported, "1");
}

export function markD7CandidateReported(): void {
  storage.set(KEYS.d7CandidateReported, "1");
}

export function hasMarkedFirstDay(): boolean {
  return storage.get(KEYS.firstDayMarked) === "1";
}

/** Returns true only the first time the user marks a day. */
export function recordFirstDayMarked(): boolean {
  if (!hasOptionalStorageConsent()) return false;
  if (hasMarkedFirstDay()) return false;
  storage.set(KEYS.firstDayMarked, "1");
  return true;
}

/** Test helper — clears funnel state. */
export function resetActivationFunnel(): void {
  storage.remove(KEYS.firstOpen);
  storage.remove(KEYS.signupAt);
  storage.remove(KEYS.firstDayMarked);
  storage.remove(KEYS.lastActiveDate);
  storage.remove(KEYS.d1Reported);
  storage.remove(KEYS.d7CandidateReported);
}
